import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { adminAPI } from '../../utils/api';

const AdminContentReviewPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [approving, setApproving] = useState(null);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await adminAPI.getReviewQueue();
      setItems(res.data.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  const approve = async (topicId) => {
    setApproving(topicId);
    try {
      await adminAPI.approveReviewTopic(topicId);
      setItems(prev => prev.filter(i => i.topic_id !== topicId));
      setExpanded(null);
      setToast('✅ Topic approved and live!');
      setTimeout(() => setToast(null), 3000);
    } catch {
      setToast('❌ Failed to approve. Try again.');
      setTimeout(() => setToast(null), 3000);
    }
    setApproving(null);
  };

  const subjects = ['all', ...new Set(items.map(i => i.subject))];
  const filtered = filter === 'all' ? items : items.filter(i => i.subject === filter);
  const subjectColor = (subject) => {
    const map = { 'Social Science': '#3B82F6', 'History': '#8B5CF6', 'Hindi': '#F59E0B' };
    return map[subject] || '#64748B';
  };

  return (
    <AdminShell>
      <div style={{ padding: '24px', maxWidth: '900px' }}>
        {toast && (
          <div style={{ position: 'fixed', top: '20px', right: '20px', background: toast.startsWith('✅') ? '#1B7F3E' : '#DC2626', color: 'white', padding: '12px 20px', borderRadius: '10px', fontWeight: 700, fontSize: '13px', zIndex: 9999 }}>
            {toast}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text)' }}>🔍 Content Review Queue</div>
          <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>Sensitive topics flagged for review before going live to students.</div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 20px' }}>
            <div style={{ fontSize: '22px', fontWeight: 900, color: '#B45309' }}>{items.length}</div>
            <div style={{ fontSize: '11px', color: '#B45309', fontWeight: 600 }}>Pending Review</div>
          </div>
          {subjects.filter(s => s !== 'all').map(s => (
            <div key={s} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 20px' }}>
              <div style={{ fontSize: '22px', fontWeight: 900, color: subjectColor(s) }}>{items.filter(i => i.subject === s).length}</div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 600 }}>{s}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {subjects.map(s => (
            <button key={s} onClick={() => setFilter(s)}
              style={{ padding: '6px 14px', borderRadius: '20px', border: `1.5px solid ${filter === s ? 'var(--brand-primary)' : 'var(--border)'}`, background: filter === s ? 'var(--brand-primary-light)' : 'white', color: filter === s ? 'var(--brand-primary)' : 'var(--muted)', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
              {s === 'all' ? `All (${items.length})` : `${s} (${items.filter(i => i.subject === s).length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎉</div>
            <div style={{ fontSize: '16px', fontWeight: 800 }}>All caught up!</div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '6px' }}>No topics pending review.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filtered.map(item => (
              <div key={item.topic_id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                  onClick={() => setExpanded(expanded === item.topic_id ? null : item.topic_id)}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: subjectColor(item.subject), flexShrink: 0 }}></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text)' }}>{item.topic_name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>Class {item.class} · {item.subject} · {item.chapter_name}</div>
                  </div>
                  <span style={{ background: '#FEF3C7', color: '#B45309', fontSize: '10px', fontWeight: 800, padding: '3px 10px', borderRadius: '20px' }}>NEEDS REVIEW</span>
                  <button onClick={e => { e.stopPropagation(); approve(item.topic_id); }} disabled={approving === item.topic_id}
                    style={{ background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', flexShrink: 0 }}>
                    {approving === item.topic_id ? '...' : '✅ Approve'}
                  </button>
                  <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{expanded === item.topic_id ? '▲' : '▼'}</span>
                </div>
                {expanded === item.topic_id && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '16px', background: '#F7F8FC' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--muted)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Content Preview — Layer 1</div>
                    <ul style={{ paddingLeft: '18px', margin: '0 0 12px 0' }}>
                      {item.layer_1_bullets.map((b, i) => (
                        <li key={i} style={{ fontSize: '13px', lineHeight: 1.7, color: 'var(--text)', marginBottom: '4px' }}>{b}</li>
                      ))}
                    </ul>
                    {item.layer_1_paragraph && (
                      <p style={{ fontSize: '13px', lineHeight: 1.7, color: 'var(--text)', background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '12px' }}>{item.layer_1_paragraph}</p>
                    )}
                    {item.layer_1_example && (
                      <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '12px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 800, color: '#2563EB', marginBottom: '6px' }}>💡 REAL-LIFE EXAMPLE</div>
                        <p style={{ fontSize: '13px', lineHeight: 1.7, color: 'var(--text)', margin: 0 }}>{item.layer_1_example}</p>
                      </div>
                    )}
                    <div style={{ marginTop: '14px' }}>
                      <button onClick={() => approve(item.topic_id)} disabled={approving === item.topic_id}
                        style={{ background: '#1B7F3E', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 20px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
                        {approving === item.topic_id ? 'Approving...' : '✅ Approve & Make Live'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminContentReviewPage;
